import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { DEMO_ACCOUNT_FLAG_KEY } from 'src/shared/services/demo-data.service';

/**
 * Which rental businesses the ACCOUNT runs. Distinct from
 * StrategyTargets.short_term_enabled / longer_term_enabled, which are
 * per-listing switches inside the Optimizer — these two are account-wide and
 * decide whether the short-term and long-term feature sets appear at all
 * (Leasing, Rent Collection, Deposits, eSignature, … for long-term).
 */
export interface RentalModes {
  shortTerm: boolean;
  longTerm: boolean;
}

const STORAGE_KEY = 'tidy_rental_modes';
const METADATA_KEY = 'rental_modes';

// Both on by default so a fresh account sees the whole product; the user turns
// off whichever business they don't run.
const DEFAULT_MODES: RentalModes = { shortTerm: true, longTerm: true };

/**
 * Account-level rental-mode store, persisted through the customer metadata
 * endpoint (same arbitrary-key store as `theme_mode` / `language`):
 *
 * - Writes: `addMetadata({ key: 'rental_modes', value: <json> })` — the server
 *   upserts per (customer, key) and prepends `external.` on reads.
 * - Reads: `Me.getMyData()` hydrates via {@link hydrateFromMetadata} on login,
 *   mirroring how the saved theme is restored.
 * - localStorage is only a boot cache so the nav renders with the right
 *   sections before the metadata round-trip lands.
 *
 * The shared demo account (demos@tidy.com) is deliberately excluded from BOTH
 * directions: many prospects browse it at once, so a visitor's toggle must stay
 * in their browser (localStorage) and never write to — or hydrate from — the
 * shared account's metadata.
 */
@Injectable({ providedIn: 'root' })
export class RentalModesService {

  // Client is resolved lazily (like ThemeService does) — it isn't available
  // before login, and injecting it eagerly risks a DI cycle through the
  // nav/pages that consume this service.
  private injector = inject(Injector);

  private readonly subject = new BehaviorSubject<RentalModes>(this.readCache());

  /** Emits the current modes immediately, then on every change. */
  get modes$(): Observable<RentalModes> {
    return this.subject.asObservable();
  }

  get modes(): RentalModes {
    return this.subject.value;
  }

  /** Applies a partial change (e.g. `{ longTerm: false }`), persists it, and notifies. */
  update(change: Partial<RentalModes>): void {
    const next: RentalModes = { ...this.subject.value, ...change };
    this.writeCache(next);
    this.subject.next(next);
    this.persistToMetadata(next);
  }

  /**
   * Pulls the saved modes from the metadata endpoint and applies them — called
   * from Me.getMyData() so a login on a fresh device restores the account's
   * setting. No-op for demo sessions and on any fetch/parse failure (the boot
   * cache or defaults simply stand).
   */
  async hydrateFromMetadata(): Promise<void> {
    if (this.isDemoSession()) { return; }
    try {
      const client = this.injector.get(Client);
      const rows = await client.getMetadata([`external.${METADATA_KEY}`]);
      const raw = (Array.isArray(rows) ? rows : []).find((r: any) => r?.key === `external.${METADATA_KEY}`)?.value;
      if (!raw) { return; }
      const modes = this.parse(raw);
      if (!modes) { return; }
      this.writeCache(modes);
      if (modes.shortTerm !== this.subject.value.shortTerm || modes.longTerm !== this.subject.value.longTerm) {
        this.subject.next(modes);
      }
    } catch {
      // Metadata restore is cosmetic-critical at worst — never block login on it.
    }
  }

  private persistToMetadata(modes: RentalModes): void {
    // The demo session is shared — a visitor's toggle must never write to it.
    if (this.isDemoSession()) { return; }
    try {
      const client = this.injector.get(Client);
      client.addMetadata({ key: METADATA_KEY, value: JSON.stringify(modes) });
    } catch {
      // Not logged in (or Client unavailable) — the local cache still drives the UI.
    }
  }

  private isDemoSession(): boolean {
    try {
      return localStorage.getItem(DEMO_ACCOUNT_FLAG_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private readCache(): RentalModes {
    try {
      const modes = this.parse(localStorage.getItem(STORAGE_KEY));
      return modes ?? { ...DEFAULT_MODES };
    } catch {
      return { ...DEFAULT_MODES };
    }
  }

  private writeCache(modes: RentalModes): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
    } catch {
      // Private-mode / quota failures shouldn't break the toggle — the in-memory
      // value still drives the UI for this session.
    }
  }

  private parse(raw: string | null): RentalModes | null {
    if (!raw) { return null; }
    try {
      const parsed = JSON.parse(raw);
      return {
        shortTerm: typeof parsed?.shortTerm === 'boolean' ? parsed.shortTerm : DEFAULT_MODES.shortTerm,
        longTerm: typeof parsed?.longTerm === 'boolean' ? parsed.longTerm : DEFAULT_MODES.longTerm,
      };
    } catch {
      return null;
    }
  }
}
